'use client'

import { useRouter } from 'next/navigation'
import { parseCookies } from 'nookies'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'

import configuration from '~/configuration'

import { useRouterContext } from '~/lib/next/use-router-context'

import useSubscriptionPlan from '~/components/Subscription/useSubscriptionPlan'

import { SESSION_COOKIE_NAME, SESSION_COOKIE_SIGNED_TOKEN } from '~/core/constants/cookies'
import { destroySessionCookiesClient } from '~/core/utilities/catch-api-error'
import type { redirectTo } from '~/lib/next/authUtilities'
import { handleAuthenticationInternalFlow } from '~/lib/next/authUtilities'
import useBoundStore from '~/lib/store'
import {
  getSlugTenantFromCareerHubUrl,
  isCPageUrl,
  isProtectedRoute
} from '~/lib/utilities/pathname'

interface InternalProviderProps {
  children: ReactNode
}

function InternalFlowProvider({ children }: InternalProviderProps) {
  const { params, searchParams, pathnameWithoutLocale, asPathWithoutLocale } = useRouterContext()
  const router = useRouter()
  const { user } = useBoundStore()
  const { data: subscriptionData } = useSubscriptionPlan()

  const [isLoading, setIsLoading] = useState(true)

  const cookies = parseCookies()

  // Check if current route is protected
  const isPrivateRoute = useMemo(() => {
    return isProtectedRoute(asPathWithoutLocale)
  }, [asPathWithoutLocale])

  // Main authentication effect
  useEffect(() => {
    if (!searchParams || !params || !pathnameWithoutLocale) return

    const authenticationMiddleware = async () => {
      try {
        // For public routes, show component immediately
        if (!isPrivateRoute) {
          setIsLoading(false)
          return
        }

        // For private routes, run authentication flow
        if (
          isPrivateRoute &&
          (!cookies[SESSION_COOKIE_NAME] || !cookies[SESSION_COOKIE_SIGNED_TOKEN])
        ) {
          destroySessionCookiesClient(window.location.pathname)
          window.location.href = configuration.path.login
          return
        }

        const planInfo = subscriptionData && {
          isExpired: subscriptionData.isExpired,
          defaultPlan: subscriptionData.defaultPlan
        }

        const tenantSlug = getSlugTenantFromCareerHubUrl(asPathWithoutLocale)
        // Run authentication flow
        const flowResult = handleAuthenticationInternalFlow({
          cookies,
          user,
          resolvedUrl: asPathWithoutLocale,
          query: params,
          planInfo
        })

        const redirectResponse = flowResult as ReturnType<typeof redirectTo>

        // Handle redirects
        if (redirectResponse.redirect) {
          // Pages that should not redirect (CareerHub login/register)
          const pagesNotRedirect = tenantSlug
            ? [
                configuration.path.careerHub.login(tenantSlug),
                configuration.path.careerHub.register(tenantSlug)
              ]
            : []

          const redirectSearch = pagesNotRedirect.filter(
            (item) => asPathWithoutLocale?.search(item) !== -1
          )

          // Only redirect if not on no-redirect pages
          if (redirectSearch.length === 0) {
            const redirectDestination = String(redirectResponse.redirect.destination)

            // Determine redirect method
            const PAGES_USE_WINDOW_LOCATION: string[] = [configuration.path.login]
            const useWindowLocation = PAGES_USE_WINDOW_LOCATION.some((url) =>
              redirectDestination.startsWith(url)
            )

            if (useWindowLocation) {
              window.location.href = redirectDestination
            } else {
              router.push(redirectDestination)
            }
            return
          }
        }

        setIsLoading(false)
      } catch (error) {
        setIsLoading(false)
      }
    }

    authenticationMiddleware()
  }, [
    subscriptionData,
    isPrivateRoute,
    searchParams,
    params,
    pathnameWithoutLocale,
    asPathWithoutLocale,
    user,
    router,
    cookies
  ])

  useEffect(() => {
    document.addEventListener('visibilitychange', () => {
      const cookies = parseCookies()
      const pathname = window.location.pathname
      const isPrivateRoute = isProtectedRoute(pathname)
      const hasSessionToken = !!cookies[SESSION_COOKIE_NAME]
      const hasSignedToken = !!cookies[SESSION_COOKIE_SIGNED_TOKEN]
      const hasValidAuthState = hasSessionToken && hasSignedToken
      const isStateLoggedIn = hasValidAuthState && !isPrivateRoute
      const isStateLoggedOut = !hasValidAuthState && isPrivateRoute
      if (
        document.visibilityState === 'visible' &&
        (isStateLoggedIn || isStateLoggedOut) &&
        !isCPageUrl(pathname)
      ) {
        if (isStateLoggedOut) {
          window.location.href = configuration.path.login
          return
        }
        window.location.href = configuration.path.default
      }
    })
    return () => {
      document.removeEventListener('visibilitychange', () => {})
    }
  }, [isPrivateRoute])

  // Show loading state for private routes during auth check
  if (isLoading && isPrivateRoute) {
    return <></>
  }

  return children
}

export default InternalFlowProvider
